import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
// Discount Progress Message - Real-time update
function updateDiscountProgress(cartTotal) {
  const el = document.querySelector('.cart-discount-progress');
  if (!el) return;

  const total = cartTotal / 100;
  let message = '';

  if (total < 99) {
    const remaining = Math.ceil(99 - total);
    message = `🛍️ You're <strong>$${remaining}</strong> away from <strong>5% off</strong> your order!`;
  } else if (total < 199) {
    const remaining = Math.ceil(199 - total);
    message = `🎉 You've unlocked <strong>5% off</strong>! Add <strong>$${remaining}</strong> more to unlock <strong>10% off</strong>!`;
  } else if (total < 299) {
    const remaining = Math.ceil(299 - total);
    message = `🎉 You've unlocked <strong>10% off</strong>! Add <strong>$${remaining}</strong> more to unlock <strong>15% off</strong>!`;
  } else {
    message = `🎉 You've unlocked <strong>15% off</strong> your entire order!`;
  }

  el.innerHTML = message;
}

document.addEventListener('cart:updated', function(e) {
  if (e.detail && e.detail.cart) {
    updateDiscountProgress(e.detail.cart.total_price);
  }
});

const originalFetch = window.fetch;
window.fetch = async function(...args) {
  const response = await originalFetch(...args);
  const url = args[0];
  if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
    const clone = response.clone();
    clone.json().then(data => {
      if (data.total_price !== undefined) {
        updateDiscountProgress(data.total_price);
      }
    }).catch(() => {});
  }
  return response;
};
